import React, { useEffect, useState } from "react";
import { Packages } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUpload from "@/components/ImageUpload";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import LivePreviewPane from "@/components/admin/LivePreviewPane";
import PackagePreview from "@/components/admin/PackagePreview";
import { notifyPreviewRefresh } from "@/components/admin/GlobalPreviewPane";

const empty = {
  name: "",
  slug: "",
  short_description: "",
  description_html: "",
  price: "Request a Quote",
  image_url: "",
  features: [],
  is_active: true,
  sort_order: 0,
};

const PackagesManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);
  const [featuresText, setFeaturesText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await Packages.list();
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEdit("new");
    setForm(empty);
    setFeaturesText("");
  };

  const openEdit = (p) => {
    setEdit(p.id);
    setForm({ ...empty, ...p });
    setFeaturesText((p.features || []).join("\n"));
  };

  const close = () => { setEdit(null); setForm(empty); setFeaturesText(""); };

  const save = async ({ keepOpen = false } = {}) => {
    if (!form.name) { toast.error("Name is required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      features: featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      let saved;
      if (edit === "new") {
        saved = await Packages.create(payload);
        toast.success("Package created");
      } else {
        saved = await Packages.update(edit, payload);
        toast.success("Package updated");
      }
      notifyPreviewRefresh();
      if (keepOpen) {
        if (saved && saved.id) {
          setEdit(saved.id);
          setForm({ ...empty, ...saved });
          setFeaturesText((saved.features || []).join("\n"));
        }
        await load();
      } else {
        close();
        await load();
      }
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await Packages.remove(p.id);
      toast.success("Deleted");
      notifyPreviewRefresh();
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-10" data-testid="packages-manager">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-2">Equipment</div>
          <h1 className="font-display font-black text-4xl text-white">Packages</h1>
        </div>
        <button
          onClick={openNew}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full brand-gradient text-white text-sm font-semibold"
          data-testid="new-package-button"
        >
          <Plus className="h-4 w-4" /> New Package
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="glass rounded-xl p-10 text-center text-slate-400">No packages yet. Create one to get started.</div>
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-[0.16em]">
              <tr>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Slug</th>
                <th className="text-left px-5 py-3">Price</th>
                <th className="text-left px-5 py-3">Active</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map((p) => (
                <tr key={p.id} className="hover:bg-slate-900/50" data-testid={`row-${p.slug}`}>
                  <td className="px-5 py-4 text-white">{p.name}</td>
                  <td className="px-5 py-4 text-slate-400">/{p.slug}</td>
                  <td className="px-5 py-4 text-slate-300">{p.price}</td>
                  <td className="px-5 py-4">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${p.is_active ? "bg-atc-green/15 text-atc-green" : "bg-slate-700/30 text-slate-400"}`}>
                      {p.is_active ? "Live" : "Hidden"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right space-x-2">
                    <button onClick={() => openEdit(p)} className="text-slate-300 hover:text-white" data-testid={`edit-${p.slug}`}>
                      <Pencil className="h-4 w-4 inline" />
                    </button>
                    <button onClick={() => remove(p)} className="text-slate-300 hover:text-rose-400" data-testid={`delete-${p.slug}`}>
                      <Trash2 className="h-4 w-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-slate-950 border-white/10 text-white max-w-[1400px] w-[95vw] max-h-[92vh] overflow-hidden p-0 flex flex-col">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/5 shrink-0">
            <DialogTitle>{edit === "new" ? "New package" : "Edit package"}</DialogTitle>
          </DialogHeader>
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-5 px-6 py-5 overflow-y-auto flex-1">
          <div className="space-y-4 min-w-0">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Name</label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="bg-slate-950 border-white/10 mt-1" data-testid="pkg-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Slug (URL, optional)</label>
                <Input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} className="bg-slate-950 border-white/10 mt-1" data-testid="pkg-slug" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Price label</label>
                <Input value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} className="bg-slate-950 border-white/10 mt-1" data-testid="pkg-price" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400 block mb-2">Image</label>
              <ImageUpload
                value={form.image_url}
                onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
                testId="pkg-image"
                label="Package image"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Short description</label>
              <Textarea value={form.short_description} onChange={(e) => setForm((f) => ({ ...f, short_description: e.target.value }))} className="bg-slate-950 border-white/10 mt-1" rows={2} data-testid="pkg-short" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400 block mb-1">Long description</label>
              <RichTextEditor
                value={form.description_html}
                onChange={(html) => setForm((f) => ({ ...f, description_html: html }))}
                placeholder="Describe the package in detail…"
                minHeight={140}
                testId="pkg-desc-editor"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Features (one per line)</label>
              <Textarea value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} className="bg-slate-950 border-white/10 mt-1" rows={4} data-testid="pkg-features" />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="pkg-active" />
                <span className="text-sm text-slate-300">Active</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-400">Sort:</span>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))} className="bg-slate-950 border-white/10 w-24" data-testid="pkg-sort" />
              </div>
            </div>
          </div>

          <div className="hidden lg:block sticky top-0 self-start h-[calc(92vh-9rem)]">
            <LivePreviewPane
              title="Live preview · /packages/"
              openUrl={form.slug ? `/packages/${form.slug}` : undefined}
              testId="package-live-preview"
            >
              <PackagePreview pkg={form} featuresText={featuresText} />
            </LivePreviewPane>
          </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-white/5 shrink-0">
            <button onClick={close} className="px-4 py-2 rounded-full text-sm text-slate-300 hover:text-white" data-testid="pkg-cancel">Cancel</button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 rounded-full brand-gradient text-white text-sm font-semibold disabled:opacity-60" data-testid="pkg-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesManager;
